import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface ChangelogEntry {
  date: string;
  version: string;
  title: string;
  changes: {
    type: 'added' | 'improved' | 'fixed';
    text: string;
  }[];
}

const changelog: ChangelogEntry[] = [
  {
    date: '2026-02-10',
    version: '1.5.0',
    title: 'Excel Import & Export',
    changes: [
      { type: 'added', text: 'Import data from previously exported TaxVault Excel files in Settings' },
      { type: 'added', text: 'Export all tax data as a multi-sheet Excel workbook to share with your tax preparer' },
      { type: 'added', text: 'Export button on Tax Summary page for quick access' },
      { type: 'added', text: 'Changelog page at /changelog' },
      { type: 'improved', text: 'CORS configuration updated for reliable API access from thetaxes.app' },
    ],
  },
  {
    date: '2026-02-09',
    version: '1.4.0',
    title: 'AI Conversation History & Smart Actions',
    changes: [
      { type: 'added', text: 'AI conversation history logged to DynamoDB and viewable in Settings' },
      { type: 'added', text: 'AI assistant can now create tasks, add expenses, income, deductions, track mileage, and update W-2 data' },
      { type: 'added', text: 'Expandable conversation details showing messages, responses, and actions taken' },
      { type: 'improved', text: 'AI assistant uses your actual tax data for context-aware answers' },
    ],
  },
  {
    date: '2026-02-08',
    version: '1.3.0',
    title: 'Tasks, Reminders & Info Tooltips',
    changes: [
      { type: 'added', text: 'Tasks page for tracking tax-related to-dos with due dates' },
      { type: 'added', text: 'Overdue task warnings with visual indicators' },
      { type: 'added', text: 'Task creation through the AI assistant via natural language' },
      { type: 'added', text: 'Info tooltips throughout the app explaining tax concepts' },
      { type: 'added', text: 'Email allowlist for restricting account registration' },
      { type: 'improved', text: 'Dashboard shows pending task count' },
    ],
  },
  {
    date: '2026-02-06',
    version: '1.2.0',
    title: 'Smart Import & AI Assistant',
    changes: [
      { type: 'added', text: 'Smart Import page — drag-and-drop CSV, PDF, Excel, or text files for AI-powered analysis' },
      { type: 'added', text: 'AI detects data type (crypto, stocks, expenses, income, deductions, W-2) with confidence scoring' },
      { type: 'added', text: 'Direct import from Smart Import results to the appropriate data store' },
      { type: 'added', text: 'AI Chat assistant powered by Anthropic Claude or OpenAI GPT-4' },
      { type: 'added', text: 'Lambda Function URL for Smart Import to bypass API Gateway timeout limits' },
      { type: 'improved', text: 'PDF processing via AWS Textract with async polling for large documents' },
    ],
  },
  {
    date: '2026-02-04',
    version: '1.1.0',
    title: 'Investments & Tax Calculations',
    changes: [
      { type: 'added', text: 'Crypto import from Gemini, Kraken, Trust Wallet, and Coinbase' },
      { type: 'added', text: 'Stock import from Fidelity with capital gains tracking' },
      { type: 'added', text: 'Cost basis calculation using FIFO, LIFO, or Specific ID methods' },
      { type: 'added', text: 'Wash sale detection for crypto and stock trades' },
      { type: 'added', text: 'Tax Summary page with federal tax bracket calculations' },
      { type: 'added', text: 'Deductions page with automatic eligible deduction suggestions' },
      { type: 'added', text: 'Claim of Right (IRC 1341) support for repayment scenarios' },
      { type: 'added', text: 'Year-over-year analytics with multi-year comparison charts' },
      { type: 'added', text: 'Data migration between tax years' },
    ],
  },
  {
    date: '2026-02-01',
    version: '1.0.0',
    title: 'Initial Release',
    changes: [
      { type: 'added', text: 'Email OTP authentication via AWS SES' },
      { type: 'added', text: 'Dashboard with income, deductions, and tax liability overview' },
      { type: 'added', text: 'Multi-business management supporting 9 entity types' },
      { type: 'added', text: 'Expense tracking with 25+ categories and CSV import' },
      { type: 'added', text: 'Income tracking with 1099 status and CSV import' },
      { type: 'added', text: 'Document upload to S3 with AWS Textract extraction for W-2s, 1099s, 1098s, K-1s' },
      { type: 'added', text: 'Export to JSON, CSV, and TaxAct-compatible format' },
      { type: 'added', text: 'Settings page with AI provider configuration and data management' },
      { type: 'added', text: 'Dark mode UI with responsive design' },
      { type: 'added', text: 'Fully serverless AWS infrastructure via CDK (Lambda, DynamoDB, S3, CloudFront, API Gateway)' },
    ],
  },
];

const typeBadge = {
  added: 'bg-green-500/10 text-green-500',
  improved: 'bg-blue-500/10 text-blue-500',
  fixed: 'bg-yellow-500/10 text-yellow-500',
};

const typeLabel = {
  added: 'Added',
  improved: 'Improved',
  fixed: 'Fixed',
};

export function ChangelogPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/"
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            title="Back to app"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold">Changelog</h1>
            <p className="text-muted-foreground mt-1">
              New features, improvements, and fixes for TaxVault.
            </p>
          </div>
        </div>

        <div className="space-y-10">
          {changelog.map((entry) => (
            <div key={entry.version} className="relative">
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-sm font-mono text-muted-foreground">
                  {entry.date}
                </span>
                <span className="px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full">
                  v{entry.version}
                </span>
              </div>

              <h2 className="text-xl font-semibold mb-4">{entry.title}</h2>

              <ul className="space-y-2">
                {entry.changes.map((change, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded mt-0.5 flex-shrink-0 ${typeBadge[change.type]}`}
                    >
                      {typeLabel[change.type]}
                    </span>
                    <span className="text-sm">{change.text}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 border-b border-border" />
            </div>
          ))}
        </div>

        <div className="mt-12 text-center text-sm text-muted-foreground">
          TaxVault &mdash; Personal Tax Management
        </div>
      </div>
    </div>
  );
}
